import React, { useMemo, useState } from 'react';
import { MdOutlineStorefront } from 'react-icons/md';
import { SectionCard, StatTile, SubHeading, Chip, Disclosure, Segmented } from './ui';
import ChartFrame from '../charts/ChartFrame';
import BarChart from '../charts/BarChart';
import DataTable from '../charts/DataTable';
import { fmtMoney, fmtNum, fmtSigned, fmtDate, safeArray, isNum, DASH } from '../format';

const COHORT_LABELS = { new_explicit: 'New construction (explicit)', new_inferred: 'New (inferred, built this year or last)', existing: 'Existing', unknown_year: 'Unknown year' };

function StatusBlock({ title, priceKey, priceLabel, block }) {
  const s = block?.summary || {};
  return (
    <div className="le-comps-status">
      <div className="le-zone-block-title">{title}</div>
      <div className="le-stat-grid">
        <StatTile label="Listings" value={fmtNum(s.count)} sub={`${fmtNum(s.with_sqft_count)} with square footage`} emphasis />
        <StatTile label={`Median ${priceLabel}`} value={fmtMoney(s[`${priceKey}_median`], { compact: true })} sub={s[`${priceKey}_quartiles`] && isNum(s[`${priceKey}_quartiles`].p25) ? `Middle half ${fmtMoney(s[`${priceKey}_quartiles`].p25, { compact: true })}–${fmtMoney(s[`${priceKey}_quartiles`].p75, { compact: true })}` : null} />
        <StatTile label="Median $ / sq ft" value={fmtMoney(s.price_per_sqft_median)} sub={isNum(s.living_area_sqft_median) ? `Median home ${fmtNum(s.living_area_sqft_median)} sq ft` : null} />
        <StatTile label="Median days on market" value={isNum(s.days_on_market_median) ? `${fmtNum(s.days_on_market_median)} days` : DASH} />
        <StatTile label="New construction" value={fmtNum((s.cohorts || {}).new_explicit)} sub={`${fmtNum((s.cohorts || {}).new_inferred)} more inferred · ${fmtNum((s.cohorts || {}).existing)} existing`} />
      </div>
    </div>
  );
}

export default function SaleCompsCard({ data, status }) {
  const [view, setView] = useState('active');
  const scope = data?.scope || {};
  const block = data?.[view] || {};
  const priceKey = view === 'active' ? 'asking_price' : 'last_known_asking_price';
  const priceLabel = view === 'active' ? 'asking price' : 'last-known asking price';
  const ladder = safeArray(block.premium_ladder);
  const mix = safeArray(block.bedroom_mix);
  const points = safeArray(data?.points).filter((p) => p.status === view);

  const ladderChart = useMemo(() => ({
    categories: ladder.map((r) => r.label.replace(' SF', '')),
    // the chart plots a cohort only when it has enough listings to mean something; the table keeps everything
    newExplicit: ladder.map((r) => ((r.new_explicit?.count || 0) >= 2 ? r.new_explicit.price_per_sqft_median : null)),
    existing: ladder.map((r) => ((r.existing?.count || 0) >= 2 ? r.existing.price_per_sqft_median : null)),
  }), [ladder]);

  const subtitle = data
    ? `${scope.radius_miles || DASH} mi · single-family sale listings · ${fmtNum(scope.retrieved?.active)} active, ${fmtNum(scope.retrieved?.inactive)} inactive${isNum(scope.total_available?.inactive) && scope.total_available.inactive > (scope.retrieved?.inactive || 0) ? ` of ${fmtNum(scope.total_available.inactive)}` : ''} · listing-market evidence, not closed sales`
    : null;

  return (
    <SectionCard id="le-section-comps" icon={<MdOutlineStorefront size={20} />} title="Sale comps" subtitle={subtitle} status={status} dataset={data}
      headerRight={data ? <Segmented options={[{ value: 'active', label: 'Active · asking' }, { value: 'inactive', label: 'Inactive · last-known asking' }]} value={view} onChange={setView} ariaLabel="Listing status" /> : null}>
      {data ? (
        <>
          <StatusBlock title={view === 'active' ? 'Active listings' : 'Inactive listings (left the market; not proof of sale)'} priceKey={priceKey} priceLabel={priceLabel} block={block} />

          <SubHeading>New-construction premium by home size</SubHeading>
          <ChartFrame
            subtitle={`Median $/sq ft, ${priceLabel}, explicit new construction vs existing homes in each band (bars need 2+ listings)`}
            legend={[{ id: 'new', label: 'New construction (explicit)', color: '#7c3aed' }, { id: 'existing', label: 'Existing', color: 'var(--le-s2)' }]}
            table={{
              columns: [
                { key: 'label', label: 'Band' },
                { key: 'listings', label: 'Listings', align: 'right', format: (v) => fmtNum(v) },
                { key: 'new_n', label: 'New (explicit)', align: 'right', format: (v) => fmtNum(v) },
                { key: 'new_ppsf', label: 'New $/sf', align: 'right', format: (v) => (isNum(v) ? fmtMoney(v) : DASH) },
                { key: 'ex_n', label: 'Existing', align: 'right', format: (v) => fmtNum(v) },
                { key: 'ex_ppsf', label: 'Existing $/sf', align: 'right', format: (v) => (isNum(v) ? fmtMoney(v) : DASH) },
                { key: 'premium', label: 'Premium', align: 'right', format: (v, row) => (isNum(v) ? fmtSigned(v, 1, '%') : (row.thin ? 'thin sample' : DASH)) },
                { key: 'premium_any', label: 'Premium incl. inferred', align: 'right', format: (v) => (isNum(v) ? fmtSigned(v, 1, '%') : DASH) },
              ],
              rows: ladder.map((r) => ({ label: r.label, listings: r.listings, new_n: r.new_explicit?.count, new_ppsf: r.new_explicit?.price_per_sqft_median, ex_n: r.existing?.count, ex_ppsf: r.existing?.price_per_sqft_median, premium: r.new_construction_premium_pct, premium_any: r.new_any_premium_pct, thin: Boolean(r.thin_sample) })),
            }}
            footnote="Bands are configurable per market. A premium needs at least two listings on each side; thinner bands keep their medians and are marked thin sample."
          >
            <BarChart
              categories={ladderChart.categories}
              series={[
                { id: 'new', label: 'New construction (explicit)', color: '#7c3aed', values: ladderChart.newExplicit },
                { id: 'existing', label: 'Existing', color: 'var(--le-s2)', values: ladderChart.existing },
              ]}
              yFormat={(v) => fmtMoney(v, { compact: true })}
              valueFormat={(v) => `${fmtMoney(v)}/sf`}
              height={200}
            />
          </ChartFrame>

          <div className="le-chip-row">
            {ladder.filter((r) => isNum(r.new_construction_premium_pct)).map((r) => (
              <Chip key={r.band} title={`${r.new_explicit.count} new vs ${r.existing.count} existing`}>{r.label}: {fmtSigned(r.new_construction_premium_pct, 1, '%')}</Chip>
            ))}
          </div>

          {mix.length ? (
            <Disclosure summary={`Bedroom mix · ${mix.length} configurations`}>
              <DataTable
                compact
                columns={[
                  { key: 'bedrooms', label: 'Bedrooms' },
                  { key: 'count', label: 'Listings', align: 'right', format: (v) => fmtNum(v) },
                  { key: `${priceKey}_median`, label: `Median ${priceLabel}`, align: 'right', format: (v) => fmtMoney(v) },
                  { key: 'price_per_sqft_median', label: 'Median $/sf', align: 'right', format: (v) => fmtMoney(v) },
                  { key: 'living_area_sqft_median', label: 'Median sq ft', align: 'right', format: (v) => fmtNum(v) },
                ]}
                rows={mix}
              />
            </Disclosure>
          ) : null}

          <Disclosure summary={`${fmtNum(points.length)} ${view} comparable${points.length === 1 ? '' : 's'} on the map`}>
            <DataTable
              columns={[
                { key: 'address', label: 'Address' },
                { key: 'distance_miles', label: 'Miles', align: 'right', format: (v) => (isNum(v) ? v.toFixed(2) : DASH) },
                { key: priceKey, label: priceLabel, align: 'right', format: (v) => fmtMoney(v) },
                { key: 'living_area_sqft', label: 'Sq ft', align: 'right', format: (v) => fmtNum(v) },
                { key: 'price_per_sqft', label: '$/sf', align: 'right', format: (v) => (isNum(v) ? fmtMoney(v) : DASH) },
                { key: 'bedrooms', label: 'Bd', align: 'right', format: (v) => fmtNum(v) },
                { key: 'bathrooms', label: 'Ba', align: 'right', format: (v) => (isNum(v) ? String(v) : DASH) },
                { key: 'year_built', label: 'Built', align: 'right' },
                { key: 'cohort', label: 'Cohort', format: (v) => COHORT_LABELS[v] || v },
                { key: view === 'active' ? 'listed_date' : 'removed_date', label: view === 'active' ? 'Listed' : 'Removed', format: (v) => fmtDate(v) },
              ]}
              rows={points.slice().sort((a, b) => (a.distance_miles ?? 99) - (b.distance_miles ?? 99))}
            />
          </Disclosure>

          <div className="le-footnotes">{safeArray(data.notes).map((n) => <div key={n}>{n}</div>)}</div>
          <div className="le-footnotes muted">Cohort rules: {Object.entries(scope.cohort_rules || {}).map(([k, v]) => `${COHORT_LABELS[k] || k} — ${v}`).join(' · ')}</div>
        </>
      ) : null}
    </SectionCard>
  );
}
